import { Text } from '@thanx/uikit/text'
import React from 'react'
import { longTextWrap } from 'scenes/RecoveryAI/components/longTextWrap'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

// Visual treatment a message bubble can render. Distinct from the raw
// ChatMessage role union (`'user' | 'ai' | 'system'`) because system
// messages are grouped with AI for the purpose of bubble styling and
// sender-label decisions.
export enum VisualRole {
  User = 'user',
  Ai = 'ai',
}

// The Figma treats the two roles asymmetrically — and the opposite way round from
// what this component used to do. The CUSTOMER's messages sit in a full-width grey
// bubble; the AGENT's are unbubbled prose under a small brand label. Nothing is
// dark-filled, nothing is right-aligned, and no bubble has a tail.
//   context: the previous scheme (agent in a grey05 bubble, customer in grey90 white
//   text right-aligned at 80% width) is what this replaces. The `plain` prop that
//   ENG-977 added for the greeting is gone with it — every agent message is now plain,
//   so the special case has nothing left to special-case. Measurements behind these
//   numbers: .context/plans/plan_20260817_rai-wizard-figma-parity.md §1.4.
const BUBBLE_PADDING = '10px 14px'
const BUBBLE_RADIUS_PX = 8
const MESSAGE_GAP_PX = 12
// uikit's typography tokens carry no line-height (fontFamily/weight/size/tracking
// only), so the message rhythm the Figma measures — 20px for the 14px body, 14px for
// the 10px label — has to be stated explicitly alongside the variant.
const BODY_LINE_HEIGHT_PX = 20
const SENDER_LABEL_LINE_HEIGHT_PX = 14

type Props = {
  role: VisualRole
  content: string
  senderLabel?: string
  /**
   * Render `senderLabel` for assistive tech only. The customer's messages need sender
   * attribution in the a11y tree — the two speakers are otherwise separated by nothing
   * but a background colour — while the Figma deliberately draws no "You" over them.
   * Both hold at once because the label is real DOM, just visually hidden.
   */
  labelHidden?: boolean
}

// Extracted + exported so the fill/padding decision is unit-testable directly —
// styletron's css() is a no-op in the jsdom test env, so the emitted rule can't be
// asserted through the rendered DOM.
export const buildMessageBodyStyle = (isUser: boolean): StyleObject => ({
  // Multi-paragraph copy (the greeting) arrives as one string with a blank
  // line in it — honor the newlines rather than collapsing them.
  whiteSpace: 'pre-line',
  ...longTextWrap,
  ...(isUser
    ? {
        backgroundColor: merchantTheme.colors.grey10,
        padding: BUBBLE_PADDING,
        borderRadius: `${BUBBLE_RADIUS_PX}px`,
      }
    : {}),
})

const MessageBubble = ({
  role,
  content,
  senderLabel,
  labelHidden = false,
}: Props) => {
  const [css] = useStyletron()
  const isUser = role === VisualRole.User

  return (
    <div className={css({ marginBottom: `${MESSAGE_GAP_PX}px` })}>
      {senderLabel &&
        (labelHidden ? (
          // Same DOM position as the visible label, so the a11y tree reads
          // "<speaker> <message>" in both branches and the only difference is whether
          // it paints. `sr-only` is bootstrap's, already used this way in ChatInput.
          <span className="sr-only">{senderLabel}</span>
        ) : (
          <Text.BodyText6
            bold
            tag="div"
            color="grey70"
            className={css({
              lineHeight: `${SENDER_LABEL_LINE_HEIGHT_PX}px`,
              marginBottom: '4px',
            })}
          >
            {senderLabel}
          </Text.BodyText6>
        ))}
      {/* Block-level, so the customer's bubble spans the log's full content box and
          sits flush with its padding on both sides rather than hugging its text. */}
      <div className={css(buildMessageBodyStyle(isUser))}>
        <Text.BodyText4
          tag="div"
          color="grey90"
          className={css({ lineHeight: `${BODY_LINE_HEIGHT_PX}px` })}
        >
          {content}
        </Text.BodyText4>
      </div>
    </div>
  )
}

export default MessageBubble
